/**
 * AgentConversationArea Component
 * Scrollable area displaying conversation messages
 */

import React, { useRef, useEffect } from 'react'
import { AgentMessageBubble } from './AgentMessageBubble'
import type { AgentMessage, AgentAction, Agent } from '../../types'

interface AgentConversationAreaProps {
  /**
   * List of messages in the conversation
   */
  messages: AgentMessage[]
  /**
   * Whether the agent is currently loading/thinking
   */
  isLoading?: boolean
  /**
   * Current agent for displaying avatar in messages
   */
  currentAgent?: Agent | null
  /**
   * Callback when an action button is clicked
   */
  onAction?: (action: AgentAction) => void
}

/**
 * Scrollable message list with auto-scroll and typing indicator
 */
export const AgentConversationArea: React.FC<AgentConversationAreaProps> = ({
  messages,
  isLoading = false,
  currentAgent,
  onAction,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading])

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-4"
      role="log"
      aria-live="polite"
      aria-label="Conversation messages"
    >
      {messages.length === 0 && !isLoading && (
        <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">
          <p>Start a conversation by typing a message below.</p>
        </div>
      )}

      {messages.map((message) => (
        <AgentMessageBubble
          key={message.id}
          message={message}
          agentName={currentAgent?.name}
          agentEmoji={currentAgent?.agentType}
          onAction={onAction}
        />
      ))}

      {/* Typing indicator */}
      {isLoading && (
        <div className="mb-4 flex items-start gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm">
            {currentAgent?.agentType ? (
              <span role="img" aria-label={currentAgent.name}>
                {/* Agent emoji will be rendered by parent */}
              </span>
            ) : (
              '🤖'
            )}
          </div>
          <div className="rounded-2xl rounded-tl-none bg-gray-100 px-4 py-3">
            <div className="flex items-center gap-1">
              <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" />
            </div>
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  )
}

export default AgentConversationArea
